/**
 * Browser-side Edge TTS client for the SaaS deployment.
 *
 * Microsoft's WebSocket endpoint rejects connections from non-Microsoft
 * origins, so we proxy through a Cloudflare Worker that sets the correct
 * Origin and DRM headers. The voice list is hardcoded to avoid an extra
 * round-trip.
 */

// ── Types ────────────────────────────────────────────────────────────

export interface EdgeVoice {
  name: string;
  friendlyName: string;
  locale: string;
  gender: string;
}

// ── Cloudflare Worker URL ───────────────────────────────────────────

const EDGE_TTS_WORKER_URL = 'https://cascade-edge-tts.aza-a-ali.workers.dev';

function getEdgeTTSUrl(): string {
  // Allow override via env for local dev
  const override = (import.meta as any).env?.VITE_EDGE_TTS_URL;
  if (override) return override;
  return EDGE_TTS_WORKER_URL;
}

// ── Hardcoded English voice list ─────────────────────────────────────
// Fetched from Microsoft's voice list API. Updated 2026-02.

const ENGLISH_VOICES: EdgeVoice[] = [
  {name: 'en-US-AriaNeural', friendlyName: 'Aria (Neural)', locale: 'en-US', gender: 'Female'},
  {name: 'en-US-AvaNeural', friendlyName: 'Ava (Neural)', locale: 'en-US', gender: 'Female'},
  {name: 'en-US-EmmaNeural', friendlyName: 'Emma (Neural)', locale: 'en-US', gender: 'Female'},
  {name: 'en-US-JennyNeural', friendlyName: 'Jenny (Neural)', locale: 'en-US', gender: 'Female'},
  {name: 'en-US-MichelleNeural', friendlyName: 'Michelle (Neural)', locale: 'en-US', gender: 'Female'},
  {name: 'en-US-AnaNeural', friendlyName: 'Ana (Neural)', locale: 'en-US', gender: 'Female'},
  {name: 'en-US-AndrewNeural', friendlyName: 'Andrew (Neural)', locale: 'en-US', gender: 'Male'},
  {name: 'en-US-BrianNeural', friendlyName: 'Brian (Neural)', locale: 'en-US', gender: 'Male'},
  {name: 'en-US-ChristopherNeural', friendlyName: 'Christopher (Neural)', locale: 'en-US', gender: 'Male'},
  {name: 'en-US-EricNeural', friendlyName: 'Eric (Neural)', locale: 'en-US', gender: 'Male'},
  {name: 'en-US-GuyNeural', friendlyName: 'Guy (Neural)', locale: 'en-US', gender: 'Male'},
  {name: 'en-US-RogerNeural', friendlyName: 'Roger (Neural)', locale: 'en-US', gender: 'Male'},
  {name: 'en-US-SteffanNeural', friendlyName: 'Steffan (Neural)', locale: 'en-US', gender: 'Male'},
  {name: 'en-US-AvaMultilingualNeural', friendlyName: 'AvaMultilingual (Neural)', locale: 'en-US', gender: 'Female'},
  {name: 'en-US-AndrewMultilingualNeural', friendlyName: 'AndrewMultilingual (Neural)', locale: 'en-US', gender: 'Male'},
  {name: 'en-US-BrianMultilingualNeural', friendlyName: 'BrianMultilingual (Neural)', locale: 'en-US', gender: 'Male'},
  {name: 'en-US-EmmaMultilingualNeural', friendlyName: 'EmmaMultilingual (Neural)', locale: 'en-US', gender: 'Female'},
  {name: 'en-GB-SoniaNeural', friendlyName: 'Sonia (Neural)', locale: 'en-GB', gender: 'Female'},
  {name: 'en-GB-LibbyNeural', friendlyName: 'Libby (Neural)', locale: 'en-GB', gender: 'Female'},
  {name: 'en-GB-MaisieNeural', friendlyName: 'Maisie (Neural)', locale: 'en-GB', gender: 'Female'},
  {name: 'en-GB-RyanNeural', friendlyName: 'Ryan (Neural)', locale: 'en-GB', gender: 'Male'},
  {name: 'en-GB-ThomasNeural', friendlyName: 'Thomas (Neural)', locale: 'en-GB', gender: 'Male'},
  {name: 'en-AU-NatashaNeural', friendlyName: 'Natasha (Neural)', locale: 'en-AU', gender: 'Female'},
  {name: 'en-AU-WilliamMultilingualNeural', friendlyName: 'WilliamMultilingual (Neural)', locale: 'en-AU', gender: 'Male'},
  {name: 'en-IN-NeerjaExpressiveNeural', friendlyName: 'NeerjaExpressive (Neural)', locale: 'en-IN', gender: 'Female'},
  {name: 'en-IN-NeerjaNeural', friendlyName: 'Neerja (Neural)', locale: 'en-IN', gender: 'Female'},
  {name: 'en-IN-PrabhatNeural', friendlyName: 'Prabhat (Neural)', locale: 'en-IN', gender: 'Male'},
  {name: 'en-CA-ClaraNeural', friendlyName: 'Clara (Neural)', locale: 'en-CA', gender: 'Female'},
  {name: 'en-CA-LiamNeural', friendlyName: 'Liam (Neural)', locale: 'en-CA', gender: 'Male'},
  {name: 'en-IE-EmilyNeural', friendlyName: 'Emily (Neural)', locale: 'en-IE', gender: 'Female'},
  {name: 'en-IE-ConnorNeural', friendlyName: 'Connor (Neural)', locale: 'en-IE', gender: 'Male'},
  {name: 'en-ZA-LeahNeural', friendlyName: 'Leah (Neural)', locale: 'en-ZA', gender: 'Female'},
  {name: 'en-ZA-LukeNeural', friendlyName: 'Luke (Neural)', locale: 'en-ZA', gender: 'Male'},
  {name: 'en-KE-AsiliaNeural', friendlyName: 'Asilia (Neural)', locale: 'en-KE', gender: 'Female'},
  {name: 'en-KE-ChilembaNeural', friendlyName: 'Chilemba (Neural)', locale: 'en-KE', gender: 'Male'},
  {name: 'en-NG-AbeoNeural', friendlyName: 'Abeo (Neural)', locale: 'en-NG', gender: 'Male'},
  {name: 'en-NG-EzinneNeural', friendlyName: 'Ezinne (Neural)', locale: 'en-NG', gender: 'Female'},
  {name: 'en-HK-YanNeural', friendlyName: 'Yan (Neural)', locale: 'en-HK', gender: 'Female'},
  {name: 'en-HK-SamNeural', friendlyName: 'Sam (Neural)', locale: 'en-HK', gender: 'Male'},
  {name: 'en-NZ-MollyNeural', friendlyName: 'Molly (Neural)', locale: 'en-NZ', gender: 'Female'},
  {name: 'en-NZ-MitchellNeural', friendlyName: 'Mitchell (Neural)', locale: 'en-NZ', gender: 'Male'},
  {name: 'en-PH-RosaNeural', friendlyName: 'Rosa (Neural)', locale: 'en-PH', gender: 'Female'},
  {name: 'en-PH-JamesNeural', friendlyName: 'James (Neural)', locale: 'en-PH', gender: 'Male'},
  {name: 'en-SG-LunaNeural', friendlyName: 'Luna (Neural)', locale: 'en-SG', gender: 'Female'},
  {name: 'en-SG-WayneNeural', friendlyName: 'Wayne (Neural)', locale: 'en-SG', gender: 'Male'},
  {name: 'en-TZ-ImaniNeural', friendlyName: 'Imani (Neural)', locale: 'en-TZ', gender: 'Female'},
  {name: 'en-TZ-ElimuNeural', friendlyName: 'Elimu (Neural)', locale: 'en-TZ', gender: 'Male'},
];

// ── Public API ───────────────────────────────────────────────────────

/** Get all available English Edge TTS voices (hardcoded, instant). */
export function getEdgeVoices(): EdgeVoice[] {
  return ENGLISH_VOICES;
}

/** Synthesize text to an audio Blob via the Cloudflare Worker proxy. */
export async function generateEdgeTTS(opts: {
  text: string;
  voice: string;
  rate?: string;
  pitch?: string;
}): Promise<Blob> {
  const url = getEdgeTTSUrl();

  const resp = await fetch(url, {
    method: 'POST',
    headers: {'Content-Type': 'application/json'},
    body: JSON.stringify({
      text: opts.text,
      voice: opts.voice,
      rate: opts.rate ?? '+0%',
      pitch: opts.pitch ?? '+0Hz',
    }),
  });

  if (!resp.ok) {
    const err = await resp.json().catch(() => ({error: 'Synthesis failed'}));
    throw new Error((err as any).error || `HTTP ${resp.status}`);
  }

  return resp.blob();
}
